<template>
  <generic-modal
    @close="$emit('close')"
    :title="`Onderhoudstickets aanmaken voor ${caseIds.length} cases`"
    :show-actions="false"
    width="'700px'"
  >
    <template v-if="step === 'form'">
      <div class="tw-text-gray-500 tw-text-xs tw-font-bold tw-mb-2">
        Elke case krijgt hetzelfde ticket. Installatiegegevens (producten, afbeeldingen, dak)
        worden per adres automatisch toegevoegd.
      </div>

      <form-field
        type="textarea"
        label="Title"
        rows="1"
        v-model="form.title"
        maxlength="70"
        placeholder="Niet te lang! max 70 karakters"
      />
      <form-field type="textarea" label="Description" rows="3" v-model="form.description" />
      <div class="tw-flex tw-flex-column tw-gap-5">
        <form-field
          label="Service team"
          :options="partners"
          null-option="Select service team"
          type="select"
          v-model="form.installer"
        />
        <form-field
          label="Category"
          :options="categories"
          type="select"
          v-model="form.category"
          null-option="Select category"
        />
      </div>
      <div class="tw-flex tw-flex-column tw-gap-5">
        <form-field
          label="Maintenance type"
          :options="maintenanceTypes"
          null-option="Select type"
          type="select"
          v-model="form.type"
        />
        <form-field label="Priority" :options="priority" type="select" v-model="form.priority" />
      </div>
      <div class="tw-flex tw-flex-column tw-gap-5">
        <form-field
          label="Ref number for billing"
          v-model="form.refNumber"
          placeholder="Ref number for billing"
        />
        <form-field label="Number of people needed" type="number" v-model="form.teamSize" />
      </div>
      <form-field type="textarea" label="Notes about key/access" v-model="form.note" />

      <div class="tw-flex tw-justify-end tw-mt-4">
        <button class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded tw-mr-2" @click="$emit('close')">
          Annuleren
        </button>
        <button
          class="tw-bg-green-600 tw-text-white tw-py-2 tw-px-4 tw-rounded disabled:tw-opacity-40"
          :disabled="!valid || batchLoading"
          @click="runDryRun"
        >
          Controleren
        </button>
      </div>
    </template>

    <template v-else>
      <div class="tw-text-sm tw-mb-2" v-if="step === 'preview'">
        Controleer de cases. Er wordt nog niets aangemaakt.
      </div>
      <div class="tw-max-h-80 tw-overflow-y-auto tw-border tw-border-gray-200 tw-rounded">
        <div
          v-for="row in batchResult.data"
          :key="row.case_id"
          class="tw-flex tw-justify-between tw-gap-2 tw-px-2 tw-py-1 tw-border-b tw-border-gray-100 tw-text-sm"
        >
          <div>
            {{ row.address || `Case #${row.case_id}` }}
            <span v-if="row.no_installation_data" class="tw-text-orange-500 tw-text-xs">
              geen installatiedata
            </span>
            <div v-if="row.existing_tickets?.length" class="tw-text-orange-500 tw-text-xs">
              Heeft al een ticket:
              <a
                v-for="ticket in row.existing_tickets"
                :key="ticket.id"
                :href="`https://tickets.norm.eu/v2/tickets/${ticket.id}`"
                target="_blank"
                class="hover:tw-underline tw-mr-1"
                :title="ticket.title"
              >
                #{{ ticket.id }} ({{ ticketStatuses[ticket.status] || ticket.status }})
              </a>
            </div>
          </div>
          <div v-if="row.result === 'can_create'" class="tw-text-green-600">Wordt aangemaakt</div>
          <div
            v-else-if="row.result === 'skipped_no_installation_data'"
            :class="allowMissing ? 'tw-text-green-600' : 'tw-text-orange-500'"
          >
            {{ allowMissing ? 'Wordt aangemaakt' : 'Overgeslagen, geen installatiegegevens' }}
          </div>
          <a
            v-else-if="row.result === 'created'"
            :href="`https://tickets.norm.eu/v2/tickets/${row.ticket_id}`"
            target="_blank"
            class="tw-text-green-600 hover:tw-underline"
          >
            Ticket #{{ row.ticket_id }} aangemaakt
          </a>
          <div v-else-if="row.result === 'skipped_case_status'" class="tw-text-orange-500">
            Overgeslagen (case is {{ row.case_status }})
          </div>
          <div v-else-if="row.result === 'no_project'" class="tw-text-red-500">
            Geen project gevonden
          </div>
          <div v-else-if="row.result === 'case_not_found'" class="tw-text-red-500">
            Case niet gevonden
          </div>
          <div v-else-if="row.result === 'error'" class="tw-text-red-500">Mislukt</div>
          <div v-else class="tw-text-red-500">{{ row.result }}</div>
        </div>
      </div>
      <div class="tw-text-sm tw-font-bold tw-text-gray-500 tw-mt-2">
        {{ summaryText }}
      </div>

      <label
        v-if="step === 'preview' && missingCount > 0"
        class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-mt-2 tw-cursor-pointer"
      >
        <input type="checkbox" v-model="allowMissing" />
        Maak ook tickets voor {{ missingCount }} adressen zonder installatiegegevens
        (producten en afbeeldingen blijven dan leeg)
      </label>

      <div class="tw-flex tw-justify-end tw-mt-4">
        <template v-if="step === 'preview'">
          <button class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded tw-mr-2" @click="step = 'form'">
            Terug
          </button>
          <button
            class="tw-bg-green-600 tw-text-white tw-py-2 tw-px-4 tw-rounded disabled:tw-opacity-40"
            :disabled="creatableCount === 0 || batchLoading"
            @click="runCreate"
          >
            {{ batchLoading ? 'Bezig...' : `Maak ${creatableCount} tickets aan` }}
          </button>
        </template>
        <button
          v-else
          class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded"
          @click="$emit('close')"
        >
          Sluiten
        </button>
      </div>
    </template>
  </generic-modal>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { CASE_CATEGORIES, MAINTENANCE_TYPES, TICKET_PRIORITIES, ticketStatuses } from '@/constants'
import fetchData from '@/util/fetch'
import useBatchSubmit from '@/util/useBatchSubmit'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'

defineEmits(['close'])

const props = defineProps({
  caseIds: {
    type: Array,
    required: true,
  },
  initialTitle: {
    type: String,
    default: '',
  },
})

const maintenanceTypes = MAINTENANCE_TYPES
const categories = CASE_CATEGORIES
const priority = TICKET_PRIORITIES

const form = reactive({
  installer: '',
  priority: 'Normal',
  category: 'O&M',
  refNumber: '',
  teamSize: 1,
  note: '',
  type: '',
  description: '',
  title: props.initialTitle.slice(0, 70),
})

const getPartners = () => fetchData('/tickets/api/v1/partners')
const partnersQuery = useQuery({
  queryKey: ['partners'],
  queryFn: getPartners,
})
const partners = computed(() => partnersQuery.data?.value?.data.map((p) => p.legacy_name) || [])

const valid = computed(
  () =>
    !!form.installer && !!form.type && !!form.description && !!form.category && !!form.title.trim()
)

const allowMissing = ref(false)

const { step, batchResult, batchLoading, runDryRun, runCreate } = useBatchSubmit({
  url: '/tickets/api/v1/tickets/batch',
  buildBody: () => ({
    case_ids: props.caseIds,
    partner: form.installer,
    title: form.title,
    description: form.description,
    category: form.category,
    malfunction_category: form.type,
    priority: form.priority,
    client_reference: form.refNumber,
    required_team_size: Number(form.teamSize) || 1,
    note: form.note,
    allow_missing_installation_data: allowMissing.value,
  }),
  invalidateKeys: [['cases'], ['tickets']],
  createdMessage: (created) => `${created} tickets aangemaakt`,
})

const summary = computed(() => batchResult.value?.summary || {})

// the opt-in checkbox adjusts the preview locally instead of re-running the dry run
const creatableCount = computed(
  () =>
    (summary.value.can_create || 0) +
    (allowMissing.value ? summary.value.skipped_no_installation_data || 0 : 0)
)

const withExistingCount = computed(
  () => batchResult.value?.data?.filter((row) => row.existing_tickets?.length).length || 0
)

const missingCount = computed(
  () => batchResult.value?.data?.filter((row) => row.no_installation_data).length || 0
)

const summaryText = computed(() => {
  const parts = []
  if (step.value === 'preview') {
    parts.push(`${creatableCount.value} aan te maken`)
  } else {
    parts.push(`${summary.value.created || 0} aangemaakt`)
  }
  if (!allowMissing.value && summary.value.skipped_no_installation_data) {
    parts.push(`${summary.value.skipped_no_installation_data} zonder installatiegegevens`)
  }
  if (summary.value.skipped_case_status) parts.push(`${summary.value.skipped_case_status} overgeslagen`)
  if (withExistingCount.value) parts.push(`${withExistingCount.value} met een bestaand ticket`)
  if (summary.value.no_project) parts.push(`${summary.value.no_project} zonder project`)
  if (summary.value.case_not_found) parts.push(`${summary.value.case_not_found} niet gevonden`)
  if (summary.value.error) parts.push(`${summary.value.error} mislukt`)
  return parts.join(', ')
})
</script>
